/**
 * SCREEN_INTERVAL_CHECK — bucket: CLIENT_WITH_CACHE
 *
 * Two warnings emitted from a single rule:
 *   1. DTW exceeds total well depth (likely operator error or wrong well).
 *   2. Water level is above the screen top (water column is above the
 *      screen interval — sample integrity at risk; consult PM).
 *
 * Source of truth: src/products/data_vault/intake_validation.py:1283 onward.
 * Output (rule, severity, message, field, value, details) MUST match the
 * Python CLI byte-for-byte; the parity CI gate enforces this.
 */

import type { IntakeRow, RuleContext, RuleSpec, ValidationIssue } from '../types.js';
import { registerRule } from '../registry.js';

/** Cached well shape — mirrors the dict the sync bootstrap delivers. */
interface CachedWell {
  name: string;
  total_depth_ft?: number | null;
  casing_elevation_ft?: number | null;
  measuring_point_elevation_ft?: number | null;
  screen_top_ft?: number | null;
}

function fmt(n: number, places: 2 | 3): string {
  // Match Python's f"{x:.2f}" / round(x, 3) when JSON-serialized: trailing
  // zeros for .2f, no trailing zeros for round() (Python's default JSON).
  if (places === 2) return n.toFixed(2);
  // round-to-3-places, but emit as a number-shaped string the way Python's
  // round(x, 3) would re-serialize: trailing zeros stripped (e.g. 1.5 not 1.500).
  const rounded = Math.round(n * 1000) / 1000;
  // Number → string drops trailing zeros automatically.
  return String(rounded);
}

function lookupWell(row: IntakeRow, ctx: RuleContext): CachedWell | undefined {
  const wells = ctx.wells;
  if (!wells) return undefined;
  const wellName = String(row.well_name ?? '').trim().toLowerCase();
  if (!wellName) return undefined;
  return wells.get(wellName) as CachedWell | undefined;
}

function parseFloatOrNull(val: unknown): number | null {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val !== 'string') return null;
  const trimmed = val.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function rule(row: IntakeRow, ctx: RuleContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  const well = lookupWell(row, ctx);
  if (!well) return issues;

  const dtw = parseFloatOrNull(row.depth_to_water_ft);
  if (dtw == null) return issues;

  const wellName = String(row.well_name ?? '').trim();

  // Check 1: DTW exceeds total well depth
  const totalDepth = well.total_depth_ft;
  if (totalDepth != null && dtw > totalDepth) {
    issues.push({
      rule: 'SCREEN_INTERVAL_CHECK',
      severity: 'warning',
      message: `Depth to water (${fmt(dtw, 2)} ft) exceeds total well depth (${fmt(
        Number(totalDepth),
        2,
      )} ft)`,
      field: 'depth_to_water_ft',
      value: String(row.depth_to_water_ft ?? ''),
      details: {
        total_depth_ft: Number(totalDepth),
        well_name: wellName,
      },
    });
  }

  // Check 2: water level above screen top
  // TOC fallback: casing_elevation_ft → measuring_point_elevation_ft
  const toc =
    well.casing_elevation_ft != null
      ? Number(well.casing_elevation_ft)
      : well.measuring_point_elevation_ft != null
      ? Number(well.measuring_point_elevation_ft)
      : null;
  const screenTop = well.screen_top_ft != null ? Number(well.screen_top_ft) : null;

  if (toc != null && screenTop != null) {
    const waterElev = toc - dtw;
    const screenTopElev = toc - screenTop;
    if (waterElev > screenTopElev) {
      issues.push({
        rule: 'SCREEN_INTERVAL_CHECK',
        severity: 'warning',
        message:
          `Water level (${fmt(waterElev, 2)} ft MSL) is above screen top ` +
          `(${fmt(screenTopElev, 2)} ft MSL) — consult project manager`,
        field: 'depth_to_water_ft',
        value: String(row.depth_to_water_ft ?? ''),
        details: {
          water_elevation_ft: Number(fmt(waterElev, 3)),
          screen_top_elevation_ft: Number(fmt(screenTopElev, 3)),
          well_name: wellName,
        },
      });
    }
  }

  return issues;
}

export const screenIntervalCheckRule: RuleSpec = {
  code: 'SCREEN_INTERVAL_CHECK',
  bucket: 'CLIENT_WITH_CACHE',
  fn: rule,
};

registerRule(screenIntervalCheckRule);
